import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form';
import { CLAUDE_KEY_MESSAGES, useConnectClaudeCode } from '@/features/connections';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  ONBOARDING_KEY_DEFAULTS,
  type OnboardingKeyValues,
  onboardingKeySchema,
} from '../schemas/onboarding-key-schema';

type ClaudeKeyStepProps = { isRejected: boolean; onConnected: () => void };

export function ClaudeKeyStep({ isRejected, onConnected }: ClaudeKeyStepProps) {
  const form = useForm<OnboardingKeyValues>({
    resolver: zodResolver(onboardingKeySchema),
    defaultValues: ONBOARDING_KEY_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { handleSubmit, register, setError, clearErrors, formState } = form;
  const { connectClaudeCode } = useConnectClaudeCode();
  const isChecking = formState.isSubmitting;

  const onSubmit = async ({ environmentKey }: OnboardingKeyValues) => {
    const outcome = await connectClaudeCode(environmentKey);
    if (outcome.isConnected) {
      onConnected();
      return;
    }
    setError('environmentKey', { type: 'server', message: CLAUDE_KEY_MESSAGES[outcome.problem] });
  };

  const idleMessage = isChecking
    ? CLAUDE_KEY_MESSAGES.checking
    : isRejected
      ? CLAUDE_KEY_MESSAGES.rejected
      : CLAUDE_KEY_MESSAGES.idle;
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-2">
        <FormField name="environmentKey">
          <FormItem className="gap-2">
            <FormLabel className="font-normal text-muted">
              Create a self-hosted environment in your Claude organization, then paste its key.
            </FormLabel>
            <div className="flex flex-wrap gap-2">
              <FormControl hasDescription>
                <Input
                  size="lg"
                  isMono
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="cse_live_…"
                  disabled={isChecking}
                  className="w-auto shrink grow basis-70"
                  {...register('environmentKey', { onChange: () => clearErrors('environmentKey') })}
                />
              </FormControl>
              <Button type="submit" variant="primary" size="lg" isLoading={isChecking}>
                {isChecking ? 'Checking with Claude…' : 'Connect'}
              </Button>
            </div>
            <FormMessage description={idleMessage} />
          </FormItem>
        </FormField>
      </form>
    </FormProvider>
  );
}
